import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getSharedBranch, getSharedDocumentDownloadUrl } from '../api';
import { downloadUrl as nativeDownloadUrl, downloadBase64 as nativeDownloadBase64 } from '../utils/nativeDownload';

const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
};

const ShareViewerPage = () => {
    const { shareId } = useParams();
    const location = useLocation();
    const [accessCodeInput, setAccessCodeInput] = useState('');
    const [activeAccessCode, setActiveAccessCode] = useState('');
    const [shareDetails, setShareDetails] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [downloadId, setDownloadId] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const autoAttemptRef = useRef(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const prefill = params.get('code') || '';
        if (prefill && !autoAttemptRef.current) {
            autoAttemptRef.current = true;
            setAccessCodeInput(prefill);
        }
    }, [location.search]);

    const handleVerify = async (codeOverride) => {
        const accessCode = (codeOverride ?? accessCodeInput).trim();
        if (!accessCode) {
            setError('Enter the access code to continue.');
            return;
        }
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            const response = await getSharedBranch(shareId, accessCode);
            const data = response.data || {};
            setShareDetails(data.branch || null);
            setDocuments(Array.isArray(data.documents) ? data.documents : []);
            setActiveAccessCode(accessCode);
            setSuccessMessage('Access granted. You can now view the documents.');
        } catch (err) {
            console.error('Failed to verify share access:', err);
            const message = err?.response?.data?.message || 'Invalid access code or share link.';
            setError(message);
            setShareDetails(null);
            setDocuments([]);
            setActiveAccessCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadDocument = useCallback(async (doc) => {
        if (!doc || !activeAccessCode) return;
        setDownloadId(doc.documentId);
        setError('');
        try {
            const response = await getSharedDocumentDownloadUrl(shareId, doc.documentId, activeAccessCode);
            const { downloadUrl, fileContent, fileName, fileType } = response.data || {};
            if (downloadUrl) {
                await nativeDownloadUrl(downloadUrl, fileName || doc.fileName || 'document');
            } else if (fileContent) {
                await nativeDownloadBase64(fileContent, fileName || doc.fileName || 'document', fileType || doc.fileType || 'application/octet-stream');
            } else {
                setError('Unable to retrieve document. Please try again.');
            }
        } catch (err) {
            console.error('Failed to download shared document:', err);
            const message = err?.response?.data?.message || 'Unable to download document.';
            setError(message);
        } finally {
            setDownloadId('');
        }
    }, [activeAccessCode, shareId]);

    const handleReset = () => {
        setShareDetails(null);
        setDocuments([]);
        setActiveAccessCode('');
        setAccessCodeInput('');
        setError('');
        setSuccessMessage('');
        autoAttemptRef.current = false;
    };

    const canDownload = Boolean(activeAccessCode);

    return (
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
            <Stack spacing={4}>
                <Stack spacing={1}>
                    <Typography variant="h3" fontWeight={700}>
                        Shared Branch Documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Enter the access code provided with this barcode to view available documents.
                    </Typography>
                </Stack>

                <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                    <Box component="form" onSubmit={(e) => { e.preventDefault(); void handleVerify(); }}>
                        <Stack spacing={2.5} sx={{ p: { xs: 3, md: 4 } }}>
                            <TextField
                                label="Access code"
                                placeholder="Enter access code"
                                value={accessCodeInput}
                                onChange={(e) => setAccessCodeInput(e.target.value)}
                                autoComplete="one-time-code"
                                inputProps={{ maxLength: 32, sx: { letterSpacing: 2, fontSize: '1.25rem', textAlign: 'center' } }}
                                disabled={loading}
                                required
                            />
                            {error ? <Alert severity="error">{error}</Alert> : null}
                            {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    startIcon={<RefreshIcon />}
                                    disabled={loading}
                                >
                                    {shareDetails ? 'Refresh documents' : 'Unlock documents'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    size="large"
                                    startIcon={<ArrowBackIcon />}
                                    disabled={loading && !shareDetails}
                                    onClick={handleReset}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Paper>

                {loading && !shareDetails ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {shareDetails ? (
                    <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 3, md: 4 } }}>
                        <Stack spacing={3}>
                            <Stack spacing={0.5}>
                                <Typography variant="h4" fontWeight={700}>
                                    {shareDetails.nickname || shareDetails.branchName || shareDetails.address || 'Branch'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {shareDetails.address || shareDetails.branchId}
                                </Typography>
                            </Stack>
                            <Divider />
                            <Stack spacing={2}>
                                {documents.length === 0 ? (
                                    <Typography variant="body1" color="text.secondary">
                                        No documents are available for this branch.
                                    </Typography>
                                ) : (
                                    documents.map(doc => (
                                        <Paper
                                            key={doc.documentId}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2,
                                                p: { xs: 2, md: 2.5 },
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                justifyContent: 'space-between',
                                                gap: 2,
                                            }}
                                        >
                                            <Stack spacing={0.5}>
                                                <Typography variant="h6">
                                                    {doc.documentTitle || doc.customDocumentTitle || doc.fileName || 'Untitled'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {doc.expirationDate ? `Expires ${formatDate(doc.expirationDate)}` : 'No expiration date'}
                                                </Typography>
                                                {!doc.hasFile ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        File preview not available.
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                            <Stack direction="row" spacing={1.5}>
                                                <Button
                                                    variant="outlined"
                                                    endIcon={<DownloadForOfflineOutlinedIcon />}
                                                    disabled={!doc.hasFile || !canDownload || downloadId === doc.documentId}
                                                    onClick={() => handleDownloadDocument(doc)}
                                                >
                                                    {downloadId === doc.documentId ? 'Preparing…' : 'View'}
                                                </Button>
                                            </Stack>
                                        </Paper>
                                    ))
                                )}
                            </Stack>
                        </Stack>
                    </Paper>
                ) : null}
            </Stack>
        </Container>
    );
};

export default ShareViewerPage;
